"use client";

import { useEffect, useState, useTransition, type ReactNode } from "react";
import type { Result } from "../actions";
import { BTN_PRIMARY, Notice } from "./ui";

/** Save state for an editor: runs the save action, shows the result, and tracks unsaved changes. */
export function useSave() {
  const [pending, startTransition] = useTransition();
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const run = (action: () => Promise<Result>, success = "Saved. The website is updated.", after?: (result: Result) => void) =>
    startTransition(async () => {
      const result = await action();
      setNotice(result.ok ? { type: "ok", text: success } : { type: "error", text: result.error });
      if (result.ok) setDirty(false);
      after?.(result);
    });

  return { pending, notice, dirty, markDirty: () => setDirty(true), run };
}

/** The bar with the save button. Sticks to the bottom of the screen on phones, so it's always in reach. */
export function SaveBar({ onSave, pending, dirty, notice, children }: { onSave: () => void; pending: boolean; dirty: boolean; notice: { type: "ok" | "error"; text: string } | null; children?: ReactNode }) {
  // Ctrl/Cmd + S saves.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSave]);

  return (
    <div className="sticky bottom-0 z-20 -mx-4 mt-8 border-t border-midnight/10 bg-offwhite/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:mt-10 lg:rounded-2xl lg:border lg:px-5">
      <div className="flex flex-wrap items-center gap-3">
        {children}
        <span className="flex-1" />
        {dirty && !pending && <span className="text-sm text-midnight/70">Unsaved changes</span>}
        <button type="button" onClick={onSave} disabled={pending} className={BTN_PRIMARY}>
          {pending ? "Saving…" : "Save"}
        </button>
      </div>
      {notice && (
        <div className="mt-3">
          <Notice notice={notice} />
        </div>
      )}
    </div>
  );
}
